import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocale } from '../contexts/LocaleContext';
import type { UserWithLocale } from '../services/api/userApi';
import { LOCALE, USER } from '../constants';

const CURRENT_USER_QUERY_KEY = ['currentUser'] as const;

/**
 * Subscribes to the cached `['currentUser']` TanStack Query and writes
 * locale + user to LocaleContext + localStorage whenever the data changes.
 *
 * Why a separate hook instead of an inline effect in AuthContext:
 * - Locale persistence is a side effect of the *data*, not the *auth action*.
 *   Coupling it to AuthContext meant that adding/removing auth code risked
 *   breaking locale sync (and vice versa).
 * - This hook reads from the cache only — it does NOT trigger a fetch
 *   (no queryFn, enabled: false). AuthContext owns the fetch lifecycle for
 *   `['currentUser']`. We just observe the cached value.
 *
 * Mount inside AuthProvider so it sees the same QueryClient and reads the
 * data populated by AuthContext's useQuery on the same key.
 */
export const useSyncSessionPreferences = (): void => {
  const { setLocale } = useLocale();

  // Observer-only: enabled: false means the queryFn is never invoked. The no-op
  // queryFn exists solely to satisfy TanStack Query's runtime warning and is never
  // called. AuthContext owns the real fetch on the same key.
  const { data } = useQuery<UserWithLocale>({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: () => Promise.reject(new Error('observer-only; never called')),
    enabled: false,
  });

  useEffect(() => {
    if (!data) return;
    if (data.locale) {
      setLocale(data.locale);
      localStorage.setItem(LOCALE, data.locale);
    }
    localStorage.setItem(USER, JSON.stringify(data.user));
  }, [data, setLocale]);
};
